import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Brain,
  Award,
  Target,
  Heart,
  Lightbulb,
  Zap,
} from "lucide-react";
import Layout from "../../layout";
import { useAuth } from "../../authContext";
import { iqLevels, cognitiveEnhancementTips } from "./iqAssessmentData";

const IQAssessmentDetail = ({ onBack, onComplete }) => {
  const { url, headers } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${url}/assessment/questions?testtype=iq`,
          {
            method: "GET",
            headers: headers
          }
        );

        if (response.ok) {
          const apiResponse = await response.json();
          const data = apiResponse.data; 
          const transformedQuestions = data.questions.map((q) => ({
            id: q.id,
            category: q.category,
            question: q.question,
            options: [
              {
                option: "A",
                text: q.option1_text,
                score: q.option1_score,
                explanation: q.option1_explanation,
              },
              {
                option: "B",
                text: q.option2_text,
                score: q.option2_score,
                explanation: q.option2_explanation,
              },
              {
                option: "C",
                text: q.option3_text,
                score: q.option3_score,
                explanation: q.option3_explanation,
              },
              {
                option: "D",
                text: q.option4_text,
                score: q.option4_score,
                explanation: q.option4_explanation,
              },
            ].filter((opt) => opt.text && opt.text.trim() !== ""), 
            correctAnswer: (() => {
              if (q.option1_score === 1) return "A";
              if (q.option2_score === 1) return "B";
              if (q.option3_score === 1) return "C";
              if (q.option4_score === 1) return "D";
              if (q.option5_score === 1) return "E";
              return "A"; // fallback
            })(),
          }));

          setQuestions(transformedQuestions);
          /*console.log(
            "✅ IQ questions fetched successfully:",
            transformedQuestions.length
          );*/
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.error("❌ Error fetching IQ questions:", error);
        setError("Failed to load questions. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [url, headers]);

  const handleAnswer = (optionIndex) => {
    const currentQuestion = questions[currentStep];
    const selectedOption = currentQuestion.options[optionIndex];
    const isCorrect = selectedOption.option === currentQuestion.correctAnswer;

    const newAnswers = [
      ...answers,
      {
        questionId: currentQuestion.id,
        selectedOption: optionIndex,
        selectedAnswer: selectedOption.option,
        selectedAnswerText: selectedOption.text,
        correctAnswer: currentQuestion.correctAnswer,
        isCorrect: isCorrect,
        category: currentQuestion.category,
        explanation: selectedOption.explanation,
      },
    ];
    setAnswers(newAnswers);

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      calculateResults(newAnswers);
    }
  };

  const calculateResults = (allAnswers) => {
    const totalScore = allAnswers.filter((answer) => answer.isCorrect).length;
    const maxScore = questions.length;
    const percentage = Math.round((totalScore / maxScore) * 100);

    // Calculate IQ score based on correct answers
    // Standard IQ scale: Mean = 100, SD = 15
    // Scale based on performance relative to expected difficulty
    let iqScore;
    let classification;
    let color;
    let icon;
    let range;

    if (percentage >= 95) {
      iqScore = Math.floor(145 + Math.random() * 10); // 145-155
      classification = "Exceptionally Gifted";
      color = "from-purple-500 to-purple-700";
      icon = "🌟";
      range = "145+";
    } else if (percentage >= 85) {
      iqScore = Math.floor(130 + (percentage - 85) * 1.5); // 130-144
      classification = "Gifted";
      color = "from-indigo-500 to-purple-600";
      icon = "🧠";
      range = "130-144";
    } else if (percentage >= 75) {
      iqScore = Math.floor(115 + (percentage - 75) * 1.5); // 115-129
      classification = "Above Average";
      color = "from-green-500 to-green-600";
      icon = "🏆";
      range = "115-129";
    } else if (percentage >= 55) {
      iqScore = Math.floor(85 + (percentage - 55) * 1.5); // 85-114
      classification = "Average";
      color = "from-blue-500 to-blue-600";
      icon = "🎯";
      range = "85-114";
    } else if (percentage >= 35) {
      iqScore = Math.floor(70 + (percentage - 35) * 0.75); // 70-84
      classification = "Below Average";
      color = "from-yellow-500 to-yellow-600";
      icon = "📚";
      range = "70-84";
    } else {
      iqScore = Math.floor(50 + percentage * 0.57); // 50-69
      classification = "Needs Development";
      color = "from-orange-500 to-red-500";
      icon = "💪";
      range = "Below 70";
    }

    const categoryScores = {};

    allAnswers.forEach((answer) => {
      const category = answer.category;
      if (!categoryScores[category]) {
        categoryScores[category] = { correct: 0, total: 0 };
      }
      categoryScores[category].total++;
      if (answer.isCorrect) {
        categoryScores[category].correct++;
      }
    });

    Object.keys(categoryScores).forEach((category) => {
      const data = categoryScores[category];
      categoryScores[category].percentage = Math.round(
        (data.correct / data.total) * 100
      );
    });

    const resultData = {
      score: totalScore,
      maxScore,
      percentage,
      iqScore,
      classification,
      range,
      color,
      icon,
      categoryScores,
      answers: allAnswers,
      completedAt: new Date().toISOString(),
    };

    setResults(resultData);
    setShowResults(true);
    onComplete(resultData);
  };

  const resetAssessment = () => {
    setCurrentStep(0);
    setAnswers([]);
    setResults(null);
    setShowResults(false);
  };

  const progress =
    questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen p-4 sm:p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">🧠</div>
            <div className="text-xl font-semibold text-gray-800 mb-2">
              Loading IQ Assessment...
            </div>
            <div className="text-gray-600">Preparing your questions</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen p-4 sm:p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">❌</div>
            <div className="text-xl font-semibold text-red-600 mb-2">
              Error Loading Questions
            </div>
            <div className="text-gray-600 mb-4">{error}</div>
            <button
              onClick={onBack}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (questions.length === 0) {
    return (
      <Layout>
        <div className="min-h-screen p-4 sm:p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">📝</div>
            <div className="text-xl font-semibold text-gray-800 mb-2">
              No Questions Available
            </div>
            <div className="text-gray-600 mb-4">Please try again later</div>
            <button
              onClick={onBack}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen p-4 sm:p-6">
        <div className="max-w-8xl mx-auto">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 sm:mb-6 font-medium text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            Back to Mental Health Hub
          </motion.button>

          {!showResults ? (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white mb-6 sm:mb-8"
              >
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 lg:gap-6">
                  <div className="text-5xl sm:text-6xl lg:text-7xl">🧩</div>
                  <div className="text-center sm:text-left">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-1 sm:mb-2">
                      Intelligence Assessment (IQ Test)
                    </h1>
                    <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-orange-100 mb-1 sm:mb-2">
                      Test your cognitive abilities and problem-solving skills
                    </p>
                    <div className="bg-white/20 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium inline-block">
                      Question {currentStep + 1} of {questions.length}
                    </div>
                  </div>
                </div>

                <div className="w-full bg-white/20 rounded-full h-2 sm:h-3 mt-4 sm:mt-6">
                  <div
                    className="bg-white h-2 sm:h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </motion.div>

              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-6 lg:p-8"
              >
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-orange-100 text-orange-800 text-sm font-medium rounded-full">
                    {questions[currentStep].category}
                  </span>
                </div>

                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-4 sm:mb-6 text-gray-900">
                  {questions[currentStep].question}
                </h2>

                <div className="space-y-3 sm:space-y-4">
                  {questions[currentStep].options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswer(index)}
                      className="w-full text-left p-3 sm:p-4 lg:p-6 rounded-lg border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all text-sm sm:text-base group"
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 border-2 border-gray-400 group-hover:border-orange-500 rounded-full flex-shrink-0 transition-colors flex items-center justify-center font-bold text-gray-600 group-hover:text-orange-600">
                          {option.option}
                        </div>
                        <div className="flex-1">
                          <span className="block font-medium">
                            {option.text}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="flex justify-between items-center mt-6 sm:mt-8">
                  <button
                    onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                    disabled={currentStep === 0}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    Previous
                  </button>
                  <div className="text-xs sm:text-sm text-gray-500">
                    {currentStep + 1} of {questions.length}
                  </div>
                </div>
              </motion.div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6 sm:space-y-8"
            >
              <div
                className={`bg-gradient-to-r ${results.color} rounded-xl sm:rounded-2xl p-8 sm:p-12 text-white text-center`}
              >
                <div className="text-6xl sm:text-7xl mb-6">{results.icon}</div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                  Your IQ Assessment Results
                </h1>

                <div className="bg-white/20 rounded-3xl p-8 mb-6">
                  <div className="text-8xl sm:text-9xl lg:text-[12rem] font-black mb-4 leading-none">
                    {results.iqScore}
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold mb-3">
                    IQ SCORE
                  </div>
                  <div className="text-xl sm:text-2xl opacity-90 font-semibold mb-2">
                    {results.classification}
                  </div>
                  <div className="text-lg sm:text-xl opacity-80">
                    Range: {results.range}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="text-2xl font-bold">
                      {results.score}/{results.maxScore}
                    </div>
                    <div className="text-sm opacity-80">Questions Correct</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="text-2xl font-bold">
                      {results.percentage}%
                    </div>
                    <div className="text-sm opacity-80">Accuracy Rate</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="text-2xl font-bold">{results.range}</div>
                    <div className="text-sm opacity-80">IQ Range</div>
                  </div>
                </div>

                <div className="text-base sm:text-lg font-medium opacity-90">
                  Your IQ score places you in the{" "}
                  <strong>{results.classification}</strong> category
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-gray-900">
                  IQ Scale Reference
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-50">
                    <div className="text-2xl">🌟</div>
                    <div>
                      <div className="font-semibold text-purple-900">
                        Exceptionally Gifted
                      </div>
                      <div className="text-sm text-purple-700">145+</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-indigo-50">
                    <div className="text-2xl">🧠</div>
                    <div>
                      <div className="font-semibold text-indigo-900">
                        Gifted
                      </div>
                      <div className="text-sm text-indigo-700">130-144</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50">
                    <div className="text-2xl">🏆</div>
                    <div>
                      <div className="font-semibold text-green-900">
                        Above Average
                      </div>
                      <div className="text-sm text-green-700">115-129</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50">
                    <div className="text-2xl">🎯</div>
                    <div>
                      <div className="font-semibold text-blue-900">Average</div>
                      <div className="text-sm text-blue-700">85-114</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-50">
                    <div className="text-2xl">📚</div>
                    <div>
                      <div className="font-semibold text-yellow-900">
                        Below Average
                      </div>
                      <div className="text-sm text-yellow-700">70-84</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50">
                    <div className="text-2xl">💪</div>
                    <div>
                      <div className="font-semibold text-orange-900">
                        Needs Development
                      </div>
                      <div className="text-sm text-orange-700">Below 70</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={() => {
                    const shareText = `🧠 I just took an IQ assessment and scored ${results.iqScore}! 🎯\n\nThink you can beat me? 😏 Take the test at CubeCover Mental Health Hub!`;
                    if (navigator.share) {
                      navigator.share({
                        title: "My IQ Assessment Result",
                        text: shareText,
                        url: window.location.origin,
                      });
                    } else {
                      navigator.clipboard.writeText(
                        shareText + `\n${window.location.origin}`
                      );
                      alert("Result copied to clipboard!");
                    }
                  }}
                  className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  <span>🚀</span>
                  Share My Result
                </button>
                <button
                  onClick={resetAssessment}
                  className="flex-1 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors font-semibold"
                >
                  Take Test Again
                </button>
                <button
                  onClick={onBack}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold"
                >
                  Back to Hub
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default IQAssessmentDetail;
